import { forwardRef } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import type { ALIAS_TEXT_COLORS } from '@tokens/colors';
import { icons } from './icons';
import type {
  PathArgs,
  IconSetDefinitions,
  ColorIconDefinition,
  MonoIconDefinition,
} from './icons';

// Reexport of the `icon` prop type for ease of use.
export type IconName = keyof IconSetDefinitions;

export interface BaseIconProps {
  /**
   * Color of the icon. Either the same color as its container or a color from
   * the text aliases.
   */
  color?: 'inherited' | keyof typeof ALIAS_TEXT_COLORS;
  /**
   * Size of the icon in pixels.
   */
  pixelSize: number;
  /**
   * Name of the icon.
   */
  icon: IconName;
  /**
   * Accessible icon description visible only to screen readers.
   * Use only on meaningful icons (non-decoratives).
   */
  description?: string;
  /**
   * Whether the icon is used in text. This affects the icon alignment.
   */
  inline?: boolean;
  /**
   * Styles override.
   */
  className?: string;

  /** Test id string for testing purposes */
  'data-testid'?: string;
}

const getPaths = (currentIcon: ColorIconDefinition | MonoIconDefinition) => {
  return (currentIcon.paths as Array<string | PathArgs>).map(
    (path, index: number): JSX.Element => {
      if (typeof path === 'string') {
        return <path key={index} d={path} />;
      }
      return <path key={index} {...path} />;
    },
  );
};

const StyledSVG = styled('svg', {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'inline',
})<{ color?: string; inline: boolean; width: number; height: number }>`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  min-width: ${({ width }) => `${width}px`};
  min-height: ${({ height }) => `${height}px`};
  fill: ${({ color }) => tokens.color[color] || 'currentColor'};
  ${({ inline }) =>
    inline &&
    `
      position: relative;
      top: -0.075em;
    `}
  &:focus {
    outline: none;
  }
`;

/**
 * BaseIcon component.
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 * @summary Provides a low-level implementation for the Icon component.
 * @see Icon
 */
export const BaseIcon = forwardRef<SVGSVGElement, BaseIconProps>(
  (
    {
      color = 'inherited',
      pixelSize,
      icon,
      description,
      inline = false,
      className,
      ...remainingProps
    }: BaseIconProps,
    ref,
  ) => {
    const currentIcon = icons[icon];

    if (!currentIcon) {
      // eslint-disable-next-line no-console
      console.warn(`Icon ${icon} does not exist.`);
      return null;
    }

    return (
      <StyledSVG
        {...remainingProps}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        width={pixelSize}
        height={pixelSize}
        color={color}
        className={className}
        role={!!description ? 'img' : undefined}
        aria-hidden={!description}
        inline={inline}
        ref={ref}
      >
        {description && <title>{description}</title>}
        {getPaths(currentIcon)}
      </StyledSVG>
    );
  },
);

BaseIcon.displayName = 'BaseIcon';
